import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextInput, Button, Container, rem, Stack, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle } from '@tabler/icons-react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '@amplify/data/resource';
import { useCompanyInfo } from '@contexts/company-info';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { WebsiteConfirmationModal } from '@components/website-confirm-modal';
import { WebsiteCrawlModal } from '@components/website-crawl-modal';

type Company = Schema['Company'];
interface CompanyInput {
  id?: string | undefined;
  name: string;
  website: string;
}

//TODO: part of the issue, see useEffect()
// const client = generateClient<Schema>();
let client = generateClient<Schema>();

function GetStartedForm() {
  const [company, setCompany] = useState<Company>();
  const companyInfoContext = useCompanyInfo();
  const navigate = useNavigate();
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [fetchModalOpened, setFetchModalOpened] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteConfirmed, setWebsiteConfirmed] = useState(false); // TODO: maybe hold this in the context, so if they nav "back" it s correct

  const createCompany = async ({ name, website }: CompanyInput) => {
    const { data, errors } = await client.models.Company.create({
      name: name,
      website: website,
    });
    console.log('createCompany: ', data, errors);
    return data;
  };

  const updateCompany = async ({ id, name, website }: CompanyInput & { id: string }) => {
    const userAttributes = await fetchUserAttributes();

    const { data, errors } = await client.models.Company.update({
      id: id,
      name: name,
      website: website,
      firstName: userAttributes.given_name || '',
      lastName: userAttributes.family_name || '',
      email: userAttributes.email || '',
    });
    console.log('updateCompany record: ', data, errors);
    // TODO: handle error from update
    return data;
  };

  const upsertCompany = async ({ id, name, website }: CompanyInput) => {
    console.log('upsertCompany:', id, name, website);
    let data: Company | undefined;
    if (id) {
      data = await updateCompany({ id, name, website });
    } else {
      data = await createCompany({ name, website });
    }
    return data;
  };

  // const fetchCompanies = async () => {
  //   const { data } = await client.models.Company.list();
  //   console.log('## fetchCompanies', data);
  // };

  useEffect(() => {
    // TODO: the client has no models if reload the page, cmd-r.  This is a workaround to get the client
    // once the page is loaded.  Why do we need to do this!
    client = generateClient<Schema>();
    if (!client.models.Company) return;
    // TODO:  If we loose network connection then the state and context are clear?  Why?  Saw it once.
    const sub = client.models.Company.observeQuery().subscribe({
      next: ({ items }) => {
        setCompany(items[0]);
        companyInfoContext.setCompany(items[0]);
      },
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  const isValidWebsite = (url: string): boolean => {
    // TODO: this is not a good regex, but it is a start.  Does not handle localhost, etc.also need to handle at least one dot, etc
    // maybe an online service to validate the url?
    // TODO:  could use google places for the company name
    const urlRegex = /^(http|https):\/\/[^ "]+$/;
    return urlRegex.test(url);
  };

  const form = useForm({
    initialValues: {
      name: '',
      website: '',
    },
    validate: {
      name: (value) =>
        value.trim().length < 2 ? 'Company name is required and must be at least 2 letters' : null,
      website: (value) =>
        !isValidWebsite(value) ? 'Invalid website, must be https://www.example.com' : null,
    },
    // enhanceGetInputProps: (payload) => {
    //   if (!payload.form.initialized) return { disable: true };
    // },
  });

  // user has submitted the form
  const handleSubmit = (values: typeof form.values) => {
    setWebsiteUrl(values.website);
    if (!websiteConfirmed) {
      setConfirmModalOpened(true);
    }
  };

  // user has confirmed the website is correct
  const handleConfirm = () => {
    setConfirmModalOpened(false);
    setFetchModalOpened(true);
    setWebsiteConfirmed(true);

    upsertCompany({
      id: companyInfoContext?.company?.id,
      name: form.values.name,
      website: form.values.website,
    }).then((data) => {
      companyInfoContext.setCompany(data);
    });
  };

  // user wants to change the website
  const handleCancel = () => {
    setConfirmModalOpened(false);
  };

  const handleCrawled = (newDatasetId: string) => {
    setFetchModalOpened(false);
    console.log('handleCrawled crawled!! now navigate()! ', newDatasetId);
    navigate('/company-overview', { state: { datasetId: newDatasetId } });
  };

  const handleCrawlClose = () => {
    console.log('WebsiteCrawlModal closed');
  };

  useEffect(() => {
    form.setValues({ name: company?.name || '', website: company?.website || '' });
    companyInfoContext.setCompany(company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  return (
    <Container size={450} my={rem(40)}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput label="Company Name" mt="md" {...form.getInputProps('name')} />
          <TextInput
            label="Company Website"
            placeholder="https://www.example.com (copy & paste from address bar)"
            mt="md"
            rightSection={
              <Tooltip
                label="Please provide the precise URL of your company's website. We suggest opening a new tab, navigating to your website, and then copying and pasting the URL directly from the address bar. This ensures accuracy in the website address you provide to us."
                multiline
                position="top-end"
                withArrow
                transitionProps={{ transition: 'pop-bottom-right' }}
                w={rem(300)}
              >
                <IconInfoCircle size={22} stroke={2} color="blue" />
              </Tooltip>
            }
            {...form.getInputProps('website')}
            onKeyDown={() => setWebsiteConfirmed(false)}
          />

          <Button mt={rem('40px')} type="submit" fullWidth>
            Submit
          </Button>
          {/* <Button mt={rem('40px')} component={Link} to="/company-overview">
            Next
          </Button> */}
          {/* <Button mt={rem('40px')} fullWidth onClick={fetchCompanies}>
            List All
          </Button> */}
        </Stack>
      </form>
      <WebsiteConfirmationModal
        opened={confirmModalOpened}
        websiteUrl={websiteUrl}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <WebsiteCrawlModal
        websiteUrl={websiteUrl}
        websiteConfirmed={websiteConfirmed}
        opened={fetchModalOpened}
        onCrawled={handleCrawled}
        onClose={handleCrawlClose}
      />
    </Container>
  );
}

export { GetStartedForm };
